import {
  useEffect,
  useState,
  useRef,
  ComponentType,
  SetStateAction
} from 'react';
import { css } from '@emotion/react';

interface TextInputProps {
  id: string;
  name: string;
  placeholder: string;
  value?: string;
  label: string;
  disabled?: boolean;
  validation?: (arg0: string) => [boolean, string];
  Icon?: ComponentType;
  onChangeHandler?: React.Dispatch<SetStateAction<string>>;
  className?: string;
}

const containerStyles = (isValid: boolean) => css`
  display: flex;
  flex-direction: column;

  input {
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.125rem;
    letter-spacing: 0px;
    text-align: left;
    color: #091e42;
  }

  input::placeholder {
    color: #a5adba;
  }

  input:hover {
    border-radius: 4px;
    border: 2px solid #dfe1e6;
    background: #ebecf0;
  }

  input:disabled {
    border-radius: 4px;
    border: 2px solid #dfe1e6;
    background: #f4f5f7;
    color: #a5adba;
  }

  input:focus {
    border: none;
    ${isValid ? `outline: 2px solid #4C9AFF;` : `outline: 2px solid #DE350B;`};
  }
`;

const labelStyles = css`
  color: #253858;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0px;
  line-height: 1.125rem;
  margin-bottom: 4px;
  text-align: left;
`;

const inputStyles = css`
  background: #fafbfc;
  border: 2px solid #dfe1e6;
  border-radius: 4px;
  flex-shrink: 0;
  height: 40px;
  padding-left: 8px;
  width: 240px;
`;

const errorStyles = css`
  color: #de350b;
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.125rem;
  margin: 0;
  margin-top: 4px;
`;

const iconStyles = css`
  position: relative;
  top: -35px;
  left: 222px;
  width: 24px;
  height: 24px;
`;

export const TextInput = (props: TextInputProps) => {
  const {
    name,
    value,
    id,
    label,
    placeholder,
    disabled,
    validation,
    Icon,
    onChangeHandler,
    className
  } = props;
  const ref = useRef<HTMLInputElement>(null);

  const [text, setText] = useState('');
  const [isValid, setValid] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (text && validation) {
      const { 0: valid, 1: error } = validation(text);
      if (valid) {
        setError('');
      } else {
        setError(error);
      }
      setValid(valid);
    }

    if (!text) {
      setError('');
    }
  }, [text]);

  return (
    <div className={className} css={containerStyles(isValid)}>
      <label css={labelStyles} htmlFor={name}>
        {label}
      </label>
      <input
        onChange={(e) => {
          setText(e.target.value);
          onChangeHandler && onChangeHandler(e.target.value);
        }}
        css={inputStyles}
        type="text"
        ref={ref}
        name={name}
        id={id}
        value={value}
        placeholder={placeholder}
        disabled={disabled || false}
      ></input>
      {Icon && (
        <div css={iconStyles}>
          <Icon></Icon>
        </div>
      )}
      {!!error && <label css={errorStyles}>{error}</label>}
    </div>
  );
};
